import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User as UserIcon, Lock } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function Profile() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: '', email: '' });
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || '', email: user.email || '' });
  }, [user]);

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch('/auth/me', form);
      await refresh();
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.errors.generic'));
    } finally {
      setSavingProfile(false);
    }
  };

  const onSavePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error(t('auth.errors.passwordTooShort'));
      return;
    }
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: '', new_password: '', confirm: '' });
      toast.success(t('profile.passwordChanged'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.errors.generic'));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="main-content max-w-3xl">
        <h1 className="page-title">{t('profile.title')}</h1>

        <Card className="p-6 mb-5">
          <h2 className="font-semibold mb-4 flex items-center gap-2 text-slate-800">
            <UserIcon className="w-4 h-4" /> {t('profile.personal')}
          </h2>
          <form onSubmit={onSaveProfile} className="space-y-4">
            <div>
              <Label>{t('profile.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <Label>{t('profile.email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <Button type="submit" disabled={savingProfile}>
              {savingProfile ? t('common.loading') : t('profile.saveChanges')}
            </Button>
          </form>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2 text-slate-800">
            <Lock className="w-4 h-4" /> {t('profile.security')}
          </h2>
          <form onSubmit={onSavePw} className="space-y-4">
            <div>
              <Label>{t('profile.currentPassword')}</Label>
              <Input type="password" value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required autoComplete="current-password" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('profile.newPassword')}</Label>
                <Input type="password" value={pw.new_password} minLength={8}
                  onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required autoComplete="new-password" />
              </div>
              <div>
                <Label>{t('profile.confirmPassword')}</Label>
                <Input type="password" value={pw.confirm} minLength={8}
                  onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required autoComplete="new-password" />
              </div>
            </div>
            <Button type="submit" disabled={savingPw}>
              {savingPw ? t('common.loading') : t('profile.changePassword')}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
